import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError, API_BASE } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Link } from "react-router-dom";
import StatusBadge, { STATUS_LABELS, STATUS_ORDER } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, BedDouble, Car, CheckCircle2, XCircle, RefreshCw, Landmark, ExternalLink } from "lucide-react";

const MOBIKAR_STATUS_LABELS = {
  pending_mobikar: { label: "En attente MOBIKAR", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Validé MOBIKAR", cls: "bg-[#dcfce7] text-[#0e7a3f]" },
  rejected: { label: "Refusé MOBIKAR", cls: "bg-red-100 text-red-700" },
  requires_revision: { label: "Modif. demandée", cls: "bg-orange-100 text-orange-800" },
};

const PAY_STATUS_LABELS = {
  unpaid: { label: "En attente paiement", cls: "bg-gray-100 text-gray-700" },
  transfer_review: { label: "Virement à vérifier", cls: "bg-amber-100 text-amber-800" },
  transfer_rejected: { label: "Virement refusé", cls: "bg-red-100 text-red-700" },
  payment_received: { label: "Paiement reçu — à valider MOBIKAR", cls: "bg-amber-100 text-amber-800" },
  payment_received_full: { label: "Paiement intégral reçu — à valider MOBIKAR", cls: "bg-amber-100 text-amber-800" },
  payment_rejected: { label: "Paiement refusé", cls: "bg-red-100 text-red-700" },
  deposit_paid: { label: "Acompte reçu — Validé", cls: "bg-[#dcfce7] text-[#0e7a3f]" },
  paid: { label: "Payé — Validé", cls: "bg-[#dcfce7] text-[#0e7a3f]" },
};

export default function AdminBookings() {
  const { fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.post(`/bookings/${id}/status`, { status });
      toast.success(`Statut mis à jour : ${STATUS_LABELS[status] || status}`);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadContract = async (b) => {
    const token = localStorage.getItem("mbk_token");
    const res = await fetch(`${API_BASE}/bookings/${b.id}/contract.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Erreur téléchargement contrat");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contrat-${b.reference}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const mobikarReview = async (b, action) => {
    const labels = { approve: "approuver", reject: "refuser", request_revision: "demander une modification pour" };
    const notes = window.prompt(`Vous êtes sur le point de ${labels[action]} le contrat ${b.reference}.\n\nAjoutez une note (optionnel pour approve, recommandé sinon) :`, "");
    if (notes === null) return;
    try {
      await api.post(`/bookings/${b.id}/mobikar-review`, { action, notes });
      toast.success(action === "approve" ? "Contrat validé et envoyé aux parties" : action === "reject" ? "Contrat refusé — parties notifiées" : "Modification demandée — signatures réinitialisées");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const transferReview = async (b, action) => {
    const verb = action === "approve" ? "valider" : "refuser";
    const note = window.prompt(`Vous êtes sur le point de ${verb} le virement pour ${b.reference}.\n\nRéférence transmise : ${b.transfer_reference || "—"}\nMontant attendu : ${(b.pricing?.deposit || 0).toLocaleString("fr-FR")} XAF\n\nNote (${action === "reject" ? "recommandé — motif" : "optionnel"}) :`, "");
    if (note === null) return;
    try {
      await api.post(`/bookings/${b.id}/transfer-review`, { action, note });
      toast.success(action === "approve" ? "Virement validé — contrat généré et emails envoyés" : "Virement refusé — client notifié");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const validatePayment = async (b, action) => {
    const verb = action === "approve" ? "VALIDER (fonds reçus)" : "REFUSER";
    const amount = (b.pricing?.deposit || 0).toLocaleString("fr-FR");
    const method = b.payment_method === "paystack" ? "Paystack" : "Carte bancaire";
    const note = window.prompt(
      `⚠️ Vous êtes sur le point de ${verb} le paiement pour ${b.reference}.\n\n` +
      `Méthode : ${method}\n` +
      `Montant attendu : ${amount} XAF\n\n` +
      `Avez-vous vérifié que les fonds ont bien été crédités sur votre compte ?\n\n` +
      `Note (${action === "reject" ? "obligatoire — motif" : "optionnelle"}) :`,
      ""
    );
    if (note === null) return;
    try {
      await api.post(`/bookings/${b.id}/validate-payment`, { action, note });
      toast.success(action === "approve"
        ? "Paiement validé — contrat généré et envoyé aux parties"
        : "Paiement refusé — client notifié");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Réservations</h1>
      <p className="text-sm text-gray-500 mt-1">Suivi et gestion des réservations véhicules et logements. Téléchargez ou changez le statut à tout moment.</p>
      <div className="mt-6 bg-white rounded-2xl border border-gray-100 mbk-card-shadow overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="p-4 text-left">Réf.</th>
              <th className="p-4 text-left">Client</th>
              <th className="p-4 text-left">Type / Bien</th>
              <th className="p-4 text-left">Dates</th>
              <th className="p-4 text-left">Montant</th>
              <th className="p-4 text-left">Statut</th>
              <th className="p-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={7} className="p-12 text-center text-gray-400">Aucune réservation.</td></tr> :
              items.map((b) => {
                const isLodging = b.resource_type === "lodging";
                const snap = isLodging ? b.lodging_snapshot : b.vehicle_snapshot;
                const Icon = isLodging ? BedDouble : Car;
                const canDownload = b.payment_status === "deposit_paid" || b.payment_status === "paid";
                return (
                  <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                    <td className="p-4 font-mono text-xs">
                      <Link to={`/confirmation/${b.id}`} className="text-[#0a1e3f] font-bold hover:text-[#0e7a3f]">{b.reference}</Link>
                    </td>
                    <td className="p-4">{b.first_name} {b.last_name}<div className="text-xs text-gray-500">{b.email}</div></td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-[#0e7a3f]" />
                        <div>
                          <div className="font-semibold">{snap?.name || "—"}</div>
                          <div className="text-xs text-gray-500">{b.agency_snapshot?.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-xs">{b.start_date} → {b.end_date}</td>
                    <td className="p-4 font-bold text-[#0a1e3f]">{fmt(b.pricing?.subtotal)}</td>
                    <td className="p-4"><StatusBadge status={b.status} /></td>
                    <td className="p-4 space-y-2">
                      {/* Bloc paiement par carte / Paystack en attente de validation MOBIKAR */}
                      {["payment_received", "payment_received_full"].includes(b.payment_status) && (
                        <div className="mb-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px]" data-testid={`payment-review-block-${b.id}`}>
                          <div className="flex items-center gap-1 font-bold text-amber-900">
                            <CheckCircle2 className="h-3 w-3" /> Paiement reçu — à vérifier
                          </div>
                          <div className="mt-1 space-y-0.5 text-gray-700">
                            <div>Méthode : <b>{b.payment_method === "paystack" ? "Paystack" : "Carte bancaire"}</b></div>
                            <div>Reçu le : <b>{b.payment_received_at ? new Date(b.payment_received_at).toLocaleString("fr-FR") : "—"}</b></div>
                            <div className="text-amber-900 italic">Vérifiez vos fonds, puis validez pour émettre le contrat.</div>
                          </div>
                          <div className="mt-2 flex gap-1 flex-wrap">
                            <button data-testid={`validate-payment-approve-${b.id}`} onClick={() => validatePayment(b, "approve")} className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-[#0e7a3f] text-white text-[10px] font-bold hover:opacity-90">
                              <CheckCircle2 className="h-3 w-3" /> Valider le paiement
                            </button>
                            <button data-testid={`validate-payment-reject-${b.id}`} onClick={() => validatePayment(b, "reject")} className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-red-600 text-white text-[10px] font-bold hover:opacity-90">
                              <XCircle className="h-3 w-3" /> Refuser
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Historique de validation admin — affiché quand validé */}
                      {b.admin_payment_validated_at && (
                        <div className="mb-2 p-2 rounded-lg bg-[#dcfce7] border border-green-200 text-[10px]" data-testid={`payment-validated-block-${b.id}`}>
                          <div className="flex items-center gap-1 font-bold text-[#0e7a3f]">
                            <CheckCircle2 className="h-3 w-3" /> Paiement validé par MOBIKAR
                          </div>
                          <div className="mt-0.5 text-gray-700">
                            <div>Le : <b>{new Date(b.admin_payment_validated_at).toLocaleString("fr-FR")}</b></div>
                            <div>Par : <b>{b.admin_payment_validator_email || b.admin_payment_validated_by || "—"}</b></div>
                            {b.admin_payment_validation_source && <div>Source : {b.admin_payment_validation_source}</div>}
                            {b.admin_payment_validation_note && <div className="italic">Note : {b.admin_payment_validation_note}</div>}
                          </div>
                        </div>
                      )}
                      {/* Bloc paiement (virement en attente) */}
                      {b.payment_method === "transfer" && (
                        <div className="mb-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px]">
                          <div className="flex items-center gap-1 font-bold text-amber-900">
                            <Landmark className="h-3 w-3" /> Virement bancaire
                          </div>
                          <div className="mt-1 space-y-0.5">
                            {PAY_STATUS_LABELS[b.payment_status] && (
                              <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${PAY_STATUS_LABELS[b.payment_status].cls}`}>
                                {PAY_STATUS_LABELS[b.payment_status].label}
                              </span>
                            )}
                            <div className="text-gray-700 font-mono truncate max-w-[220px]" title={b.transfer_reference}>Réf : {b.transfer_reference || "—"}</div>
                            {b.transfer_proof_url && (
                              <a href={b.transfer_proof_url.startsWith("http") ? b.transfer_proof_url : `${API_BASE.replace(/\/api$/, "")}${b.transfer_proof_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#0a1e3f] hover:text-[#0e7a3f] font-bold underline">
                                <ExternalLink className="h-3 w-3" /> Justificatif
                              </a>
                            )}
                          </div>
                          {b.payment_status === "transfer_review" && (
                            <div className="mt-2 flex gap-1 flex-wrap">
                              <button data-testid={`transfer-approve-${b.id}`} onClick={() => transferReview(b, "approve")} className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-[#0e7a3f] text-white text-[10px] font-bold hover:opacity-90">
                                <CheckCircle2 className="h-3 w-3" /> Valider
                              </button>
                              <button data-testid={`transfer-reject-${b.id}`} onClick={() => transferReview(b, "reject")} className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-red-600 text-white text-[10px] font-bold hover:opacity-90">
                                <XCircle className="h-3 w-3" /> Refuser
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                        <SelectTrigger data-testid={`status-${b.id}`} className="w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[...STATUS_ORDER, "cancelled"].map((s) => (
                            <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {canDownload && (
                        <button data-testid={`admin-contract-${b.id}`} onClick={() => downloadContract(b)} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#0a1e3f] text-white text-[11px] font-bold hover:opacity-90">
                          <FileText className="h-3 w-3" /> Contrat
                        </button>
                      )}
                      {b.mobikar_status && (
                        <div className="mt-1">
                          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${MOBIKAR_STATUS_LABELS[b.mobikar_status]?.cls || "bg-gray-100 text-gray-700"}`} data-testid={`mobikar-status-${b.id}`}>
                            {MOBIKAR_STATUS_LABELS[b.mobikar_status]?.label || b.mobikar_status}
                          </span>
                        </div>
                      )}
                      {b.mobikar_status === "pending_mobikar" && (
                        <div className="mt-1 flex gap-1 flex-wrap">
                          <button data-testid={`mbk-approve-${b.id}`} onClick={() => mobikarReview(b, "approve")} title="Approuver" className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-[#0e7a3f] text-white text-[10px] font-bold hover:opacity-90">
                            <CheckCircle2 className="h-3 w-3" /> Approuver
                          </button>
                          <button data-testid={`mbk-revision-${b.id}`} onClick={() => mobikarReview(b, "request_revision")} title="Demander modification" className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-orange-500 text-white text-[10px] font-bold hover:opacity-90">
                            <RefreshCw className="h-3 w-3" /> Modif.
                          </button>
                          <button data-testid={`mbk-reject-${b.id}`} onClick={() => mobikarReview(b, "reject")} title="Refuser" className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-red-600 text-white text-[10px] font-bold hover:opacity-90">
                            <XCircle className="h-3 w-3" /> Refuser
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
